import Phaser from 'phaser';

/**
 * Lightweight projectile that flies in a straight line with Arcade physics.
 * Encapsulates spawn/init and auto-cleanup behavior.
 *
 * Usage:
 *   const bullet = new Projectile(scene, x, y, 'bullet').initVelocity(dirX, dirY, 400);
 *   scene.projectiles.add(bullet);
 */
export class Projectile extends Phaser.Physics.Arcade.Sprite {
  // Optional metadata for future logic (kept for compatibility/extension)
  public owner?: Phaser.GameObjects.GameObject;
  public damage: number = 1;

  private lifespanMs: number = 2000; // matches previous cleanup after 2s

  constructor(scene: Phaser.Scene, x: number, y: number, textureKey: string, frame?: string | number) {
    super(scene, x, y, textureKey, frame);

    // Ensure added to scene + physics
    scene.add.existing(this);
    scene.physics.add.existing(this);

    // Consistent render depth/scale with previous behavior
    this.setDepth(6);
    this.setScale(0.6);

    // Configure Arcade body for dynamic, uninterrupted motion
    const body = this.body as Phaser.Physics.Arcade.Body;
    body.setAllowGravity(false);
    body.setImmovable(false);
    body.setDrag(0, 0);
    body.setDamping(false);

    // Handle world-bounds cleanup for safety in addition to lifespan timeout
    this.setCollideWorldBounds(true);
    body.onWorldBounds = true;
    scene.physics.world.on('worldbounds', (b: Phaser.Physics.Arcade.Body) => {
      if (b.gameObject === this && this.active) {
        this.destroy();
      }
    });

    // Time-based cleanup to mirror old delayed destroy
    scene.time.delayedCall(this.lifespanMs, () => {
      if (this.active) {
        this.destroy();
      }
    });
  }

  /**
   * Initialize velocity using a normalized vector and speed.
   * dirX, dirY do not need to be normalized (we normalize internally).
   */
  public initVelocity(dirX: number, dirY: number, speed: number): this {
    const mag = Math.hypot(dirX, dirY) || 1;
    const vx = (dirX / mag) * speed;
    const vy = (dirY / mag) * speed;

    // Ensure the body is enabled and dynamic at the moment we apply velocity.
    const body = this.body as Phaser.Physics.Arcade.Body | null;
    if (body) {
      body.enable = true;
      body.setImmovable(false);
      body.setAllowGravity(false);
      body.setDrag(0, 0);
      body.setDamping(false);
    } else {
      // If physics body isn't ready yet, defer velocity to next tick.
      this.scene.events.once(Phaser.Scenes.Events.UPDATE, () => this.setVelocity(vx, vy));
    }

    this.setActive(true).setVisible(true);
    this.setVelocity(vx, vy);

    // Nudge position slightly along the trajectory to avoid immediate collision with player's body
    this.x += (vx !== 0 ? Math.sign(vx) : 0) * 0.5;
    this.y += (vy !== 0 ? Math.sign(vy) : 0) * 0.5;

    return this;
  }

  /**
   * Optional helper to set an owner (e.g., Player) and damage value.
   */
  public setMeta(owner: Phaser.GameObjects.GameObject | undefined, damage: number | undefined): this {
    this.owner = owner;
    if (typeof damage === 'number') this.damage = damage;
    return this;
  }

  // Minimal preUpdate override to ensure Arcade Sprite advances correctly
  preUpdate(time: number, delta: number): void {
    super.preUpdate(time, delta);
    // Additional per-frame checks could be placed here if needed later
  }
}

export default Projectile;